'use client';

import { StockMediaAsset } from '@/api/stock-media';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import { ImagesModalSectionProps } from '@/features/media-modal/components/common/commonTypes';
import { ICardMediaAsset, ImageProviders, MediaAssetType } from '@/types/media';
import {
  BlockStack,
  Box,
  Button,
  Card,
  Form,
  FormLayout,
  InlineStack,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStockMediaInfiniteQuery } from '../../../hooks/useStockMediaInfiniteQuery';

export type StockImagesModalSectionProps = ImagesModalSectionProps;

const StockImagesModalSection = ({
  recommendation,
  selectedMedia,
  setSelectedMedia,
  multiSelection = true,
}: StockImagesModalSectionProps) => {
  const { stockMediaQuery } = recommendation || {};
  const recommendedQuery = stockMediaQuery || '';
  const [queryInput, setQueryInput] = useState(recommendedQuery);
  const [searchQuery, setSearchQuery] = useState(recommendedQuery);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (recommendedQuery && !hasInitialized.current) {
      setQueryInput(recommendedQuery);
      setSearchQuery(recommendedQuery);
      hasInitialized.current = true;
    }
  }, [recommendedQuery]);

  const {
    media: images,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useStockMediaInfiniteQuery({
    type: MediaAssetType.image,
    query: searchQuery,
  });

  const selectedUrls = useMemo(() => new Set(selectedMedia.map((image) => image.src)), [selectedMedia]);

  const addImage = useCallback(
    (image: StockMediaAsset) => {
      const newMedia = {
        id: uuidv4(),
        provider: ImageProviders.stock,
        src: image.src,
        type: MediaAssetType.image,
      };

      setSelectedMedia((prev) => {
        if (!multiSelection) {
          return [newMedia];
        }
        return [...prev, newMedia];
      });
    },
    [setSelectedMedia, multiSelection]
  );

  const removeImage = useCallback(
    (image: StockMediaAsset) => {
      setSelectedMedia((prev) => prev.filter((existing) => existing.src !== image.src));
    },
    [setSelectedMedia]
  );

  const onImageClick = useCallback(
    (image: ICardMediaAsset) => {
      const alreadySelected = selectedUrls.has(image.src!);
      if (alreadySelected) {
        return removeImage(image as StockMediaAsset);
      }
      return addImage(image as StockMediaAsset);
    },
    [addImage, removeImage, selectedUrls]
  );

  const renderLoading = () => {
    return (
      <InlineStack align="center">
        <Spinner accessibilityLabel="Loading stock images" size="large" />
      </InlineStack>
    );
  };

  const items = useMemo(
    () => images.map((asset) => ({ id: uuidv4(), src: asset.src, type: MediaAssetType.image })),
    [images]
  );

  const renderItem = useCallback(
    (asset) => {
      return (
        <MediaAssetCard
          size="small"
          hideUseAction
          aspectRatio="portrait"
          media={asset as ICardMediaAsset}
          key={asset.id}
          selected={selectedMedia.some((media) => media.src === asset.src)}
          onSelect={onImageClick}
        />
      );
    },
    [onImageClick, selectedMedia]
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage, hasNextPage]);

  const customScrollParentRef = useRef<HTMLDivElement>(null);

  return (
    <BlockStack gap="400">
      <Card>
        <FormLayout>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              setSearchQuery(queryInput);
            }}
          >
            <TextField
              label="Search stock images"
              value={queryInput}
              onChange={setQueryInput}
              autoComplete="off"
              placeholder="Enter a search term..."
              connectedRight={<Button onClick={() => setSearchQuery(queryInput)}>Search</Button>}
            />
          </Form>
        </FormLayout>
      </Card>
      <div className="flex flex-1 flex-col" ref={customScrollParentRef}>
        <BlockStack gap="400">
          {items.length > 0 ? (
            <>
              <MediaGrid items={items} size="medium" renderItem={renderItem} />
              {hasNextPage && (
                <div className="flex justify-center py-4">
                  <Button onClick={handleLoadMore} disabled={isFetchingNextPage}>
                    Load more images
                  </Button>
                </div>
              )}
            </>
          ) : (
            searchQuery &&
            !isLoading && (
              <div className="flex h-60 items-center justify-center">
                <Box background="bg-surface-secondary" padding="600" borderRadius="300">
                  <BlockStack gap="400" align="center">
                    <div className="flex justify-center">
                      <div className="bg-surface rounded-full p-4">
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M19.5 12L12 4.5L4.5 12M19.5 19.5H4.5"
                            stroke="#5C5F62"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                    <Text as="h2" variant="headingMd" alignment="center">
                      No images found
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                      Try adjusting your search terms or use different keywords
                    </Text>
                    <Button onClick={() => setQueryInput('')}>Clear search</Button>
                  </BlockStack>
                </Box>
              </div>
            )
          )}
        </BlockStack>
        {(isLoading || isFetchingNextPage) && renderLoading()}
      </div>
    </BlockStack>
  );
};

export default memo(StockImagesModalSection);
